import React, { useState } from 'react'
import type { MarketplaceJob } from '../../../services/backendService'
import '../styles/AvailableJobsList.css'

interface AvailableJobsListProps {
  jobs: MarketplaceJob[]
  onSelectJob: (job: MarketplaceJob) => void
  /** Job IDs where the current farm has already submitted a bid (open jobs only). */
  bidJobIds?: Set<string>
  /** Job IDs where the current farm has a pending fixed-price quote. */
  quoteJobIds?: Set<string>
}

export const AvailableJobsList: React.FC<AvailableJobsListProps> = ({
  jobs,
  onSelectJob,
  bidJobIds,
  quoteJobIds
}) => {
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list')
  const [filters, setFilters] = useState({
    material: '',
    pricingModel: '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'newest' as 'newest' | 'price_low_high' | 'price_high_low'
  })

  const materialOptions = Array.from(new Set(jobs.map(job => job.material))).sort()

  const getJobPrice = (job: MarketplaceJob) => {
    if (typeof job.fixedPrice === 'number') {
      return job.fixedPrice
    }

    if (typeof job.estimate?.totalCost === 'number') {
      return job.estimate.totalCost
    }

    return null
  }

  const filteredJobs = jobs.filter(job => {
    const jobPrice = getJobPrice(job)
    const minPrice = filters.minPrice ? Number(filters.minPrice) : null
    const maxPrice = filters.maxPrice ? Number(filters.maxPrice) : null

    if (filters.material && job.material !== filters.material) return false
    if (filters.pricingModel && job.pricingModel !== filters.pricingModel) return false
    if (minPrice !== null && (!Number.isFinite(minPrice) || !jobPrice || jobPrice < minPrice)) return false
    if (maxPrice !== null && (!Number.isFinite(maxPrice) || !jobPrice || jobPrice > maxPrice)) return false
    return true
  }).sort((left, right) => {
    if (filters.sortBy === 'price_low_high') {
      const leftPrice = getJobPrice(left) ?? Number.MAX_SAFE_INTEGER
      const rightPrice = getJobPrice(right) ?? Number.MAX_SAFE_INTEGER
      return leftPrice - rightPrice
    }

    if (filters.sortBy === 'price_high_low') {
      const leftPrice = getJobPrice(left) ?? -1
      const rightPrice = getJobPrice(right) ?? -1
      return rightPrice - leftPrice
    }

    const leftDate = new Date(left.createdAt).getTime()
    const rightDate = new Date(right.createdAt).getTime()
    return rightDate - leftDate
  })

  return (
    <div className="available-jobs-list">
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filter-group">
          <label>Material</label>
          <select
            value={filters.material}
            onChange={(e) => setFilters({ ...filters, material: e.target.value })}
          >
            <option value="">All Materials</option>
            {materialOptions.map(material => (
              <option key={material} value={material}>{material}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Pricing Model</label>
          <select
            value={filters.pricingModel}
            onChange={(e) => setFilters({ ...filters, pricingModel: e.target.value })}
          >
            <option value="">All Models</option>
            <option value="bid">Bid System</option>
            <option value="fixed_price">Fixed Price</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Price Range (USD)</label>
          <div className="filter-range-row">
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.minPrice}
              onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
              placeholder="Min"
            />
            <input
              type="number"
              min="0"
              step="0.01"
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
              placeholder="Max"
            />
          </div>
        </div>

        <div className="filter-group">
          <label>Sort</label>
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as 'newest' | 'price_low_high' | 'price_high_low' })}
          >
            <option value="newest">Newest</option>
            <option value="price_low_high">Price: Low to High</option>
            <option value="price_high_low">Price: High to Low</option>
          </select>
        </div>
      </div>

      <div className="jobs-section">
        <div className="jobs-section-header">
          <h3>Available Jobs ({filteredJobs.length})</h3>
          <div className="jobs-view-toggle" aria-label="Available jobs view mode">
            <button
              type="button"
              className={`jobs-view-toggle-btn ${viewMode === 'card' ? 'is-active' : ''}`}
              onClick={() => setViewMode('card')}
            >
              Cards
            </button>
            <button
              type="button"
              className={`jobs-view-toggle-btn ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
        </div>
        <div className={`jobs-list ${viewMode !== 'card' ? 'jobs-list-view' : ''}`}>
          {filteredJobs.length === 0 ? (
            <div className="empty-state">
              <p>No jobs match your filters</p>
            </div>
          ) : viewMode === 'list' ? (
            <table className="available-jobs-table" aria-label="Available marketplace jobs">
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">Material</th>
                  <th scope="col">Deadline</th>
                  <th scope="col">Doctor</th>
                  <th scope="col">Pricing</th>
                  <th scope="col">Price</th>
                  <th scope="col">Status</th>
                  <th scope="col" aria-label="Action"></th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map(job => {
                  const hasBid = bidJobIds?.has(job.id) ?? false
                  const hasQuote = quoteJobIds?.has(job.id) ?? false
                  const price = getJobPrice(job)
                  return (
                    <tr
                      key={job.id}
                      className={`available-jobs-row ${hasBid || hasQuote ? 'is-engaged' : ''}`.trim()}
                      onClick={() => onSelectJob(job)}
                    >
                      <td>
                        <span className="available-jobs-title">{job.title}</span>
                      </td>
                      <td>{job.material}</td>
                      <td>{job.deadline}</td>
                      <td>{job.doctorName} <span className="muted">★ {job.doctorRating}</span></td>
                      <td>{job.pricingModel === 'bid' ? 'Bid' : 'Fixed'}</td>
                      <td>{price !== null ? `$${price.toFixed(2)}` : '—'}</td>
                      <td>
                        {hasQuote ? (
                          <span className="status-chip is-quoted">Quote pending</span>
                        ) : hasBid ? (
                          <span className="status-chip is-bid">Bid submitted</span>
                        ) : (
                          <span className="status-chip">Open</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-view-job"
                          onClick={(event) => {
                            event.stopPropagation()
                            onSelectJob(job)
                          }}
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            filteredJobs.map(job => {
              const hasBid = bidJobIds?.has(job.id) ?? false
              const hasQuote = quoteJobIds?.has(job.id) ?? false
              return (
              <div
                key={job.id}
                className={`job-item ${hasBid || hasQuote ? 'job-item-engaged' : ''}`.trim()}
                onClick={() => onSelectJob(job)}
              >
                <div className="job-item-header">
                  <h4>{job.title}</h4>
                  <span className="pricing-badge">
                    {job.pricingModel === 'bid' ? 'Bid' : 'Fixed'}
                  </span>
                  {hasQuote && <span className="status-chip is-quoted">Quote pending</span>}
                  {!hasQuote && hasBid && <span className="status-chip is-bid">Bid submitted</span>}
                </div>

                <p className="job-item-description">{job.description}</p>

                <div className="job-item-specs">
                  <span className="spec">
                    <strong>Material:</strong> {job.material}
                  </span>
                  <span className="spec">
                    <strong>Deadline:</strong> {job.deadline}
                  </span>
                  <span className="spec">
                    <strong>Doctor:</strong> {job.doctorName} (⭐ {job.doctorRating})
                  </span>
                  {job.pricingModel === 'fixed_price' && typeof job.fixedPrice === 'number' && (
                    <span className="spec">
                      <strong>Listed Price:</strong> ${job.fixedPrice.toFixed(2)}
                    </span>
                  )}
                  {job.pricingModel === 'bid' && typeof job.estimate?.totalCost === 'number' && (
                    <span className="spec">
                      <strong>Estimated Cost Floor:</strong> ${job.estimate.totalCost.toFixed(2)}
                    </span>
                  )}
                </div>

                <button className="btn-view-job">View Details →</button>
              </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

